/**
 * Валидация и нормализация номеров телефонов
 */

import {
  parsePhoneNumberWithError as libParsePhoneNumber,
  parseIncompletePhoneNumber as libParseIncompletePhoneNumber,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js';

/**
 * Сравнивает два номера телефона, приводя их к нормализованному виду
 * @param phone1 - первый номер телефона
 * @param phone2 - второй номер телефона
 * @returns true если номера одинаковые после нормализации
 */
export function comparePhones(
  phone1: string | number | null | undefined,
  phone2: string | number | null | undefined,
): boolean {
  if (!phone1 || !phone2) {
    return false;
  }

  // Парсим оба номера
  const phoneNumber1 = parsePhoneNumber(phone1);
  const phoneNumber2 = parsePhoneNumber(phone2);

  // Если оба распарсены, сравниваем их международные номера
  if (phoneNumber1 && phoneNumber2) {
    return phoneNumber1.number === phoneNumber2.number;
  }

  // Если парсинг не удался, сравниваем как строки (fallback)
  return String(phone1) === String(phone2);
}

/**
 * Парсит номер телефона с определением страны
 * @param phone - номер телефона в любом формате
 * @param defaultCountry - страна по умолчанию (если не удалось определить из номера)
 * @returns объект PhoneNumber или null если парсинг не удался
 *
 * @example
 * parsePhoneNumber('+79991234567') // { country: 'RU', ... }
 * parsePhoneNumber('89991234567', 'RU') // { country: 'RU', ... }
 * parsePhoneNumber('9991234567', 'RU') // { country: 'RU', ... }
 */
export function parsePhoneNumber(
  phone: string | number | null | undefined,
  defaultCountry: CountryCode = 'RU',
): PhoneNumber | null {
  if (!phone) {
    return null;
  }

  const phoneStr = String(phone);

  // Бэкенд хранит телефон только цифрами без «+». Без «+» libphonenumber-js
  // подставит defaultCountry RU и склеит «375…» в «+7375…» — невалидный номер.
  // Сначала пробуем как международный (с «+»), и только если результат не валиден —
  // фолбэк на defaultCountry.
  const withPlus = phoneStr.startsWith('+') ? phoneStr : '+' + phoneStr.replace(/\D/g, '');
  try {
    const result = libParsePhoneNumber(withPlus, {});
    if (result.isValid()) {
      return result;
    }
  } catch {
    // INVALID_COUNTRY / TOO_SHORT — падаем в фолбэк ниже
  }

  try {
    return libParsePhoneNumber(phoneStr, { defaultCountry });
  } catch {
    return null;
  }
}

/**
 * Проверяет, является ли номер телефона валидным международным номером
 * @param phone - номер телефона в любом формате
 * @returns true если номер валиден
 *
 * @example
 * isValidInternationalPhone('+79991234567') // true (Россия)
 * isValidInternationalPhone('+14155551234') // true (США)
 * isValidInternationalPhone('+375291234567') // true (Беларусь)
 * isValidInternationalPhone('123') // false
 */
export function isValidInternationalPhone(phone: string | number | null | undefined): boolean {
  if (!phone) {
    return false;
  }

  const phoneNumber = parsePhoneNumber(phone);
  return phoneNumber?.isValid() ?? false;
}

/**
 * Парсит неполный номер телефона, оставляя только цифры и ведущий +
 * Удаляет все пунктуационные символы и конвертирует экзотические цифры в обычные
 *
 * @param phone - частично введённый номер телефона
 * @returns очищенный номер (только цифры и + в начале)
 *
 * @example
 * parseIncompletePhoneNumber('8 (800) 555') // '8800555'
 * parseIncompletePhoneNumber('+7 800 555') // '+7800555'
 * parseIncompletePhoneNumber('+٤٤٢٣٢٣٢٣٤') // '+442323234' (арабские цифры)
 */
export function parseIncompletePhoneNumber(phone: string | number | null | undefined): string {
  if (!phone) {
    return '';
  }

  return libParseIncompletePhoneNumber(String(phone));
}
